import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

import { isReaderAway, notifyWhileAway, onceReaderReturns } from '@/lib/desktopNotifications'
import { type PricedQuoteCard, type QuoteCardData, QuoteCardStage } from './quoteCardTypes'

/** A price a carrier sent. The customer's own upload and our estimate are not news from a carrier. */
function isCarrierPrice(quote: QuoteCardData): quote is PricedQuoteCard {
  return quote.stage === QuoteCardStage.PRICED && !quote.isCustomerUploaded && !quote.isEstimateOnly
}

// By the lines that priced, not the card: a carrier's lines fold into one card, whose memberIds also
// lists the lines still waiting, so a line that prices later would read as already known.
function pricedIndicationIds(quote: PricedQuoteCard): string[] {
  const ids = quote.coverages.flatMap((coverage) => (coverage.indicationId ? [coverage.indicationId] : []))
  return ids.length ? ids : [quote.id]
}

/** "Progressive", "Progressive and GEICO", "Progressive, GEICO and 2 more". */
function carrierList(names: string[]): string {
  if (names.length <= 2) return names.join(' and ')
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`
}

// A price, not a quote: the card calls a carrier's estimate a "Carrier estimate".
function pricesNotice(arrived: PricedQuoteCard[]): { title: string; body: string } {
  const carriers = [...new Set(arrived.map((quote) => quote.carrierName))]
  if (arrived.length === 1) {
    return { title: `New price from ${carriers[0]}`, body: 'Open Cai to see it.' }
  }
  return {
    title: `${arrived.length} new prices`,
    body: `From ${carrierList(carriers)}. Open Cai to compare them.`,
  }
}

/** "New quote from Rocklake", "3 new quotes from Progressive, GEICO, and 1 more". */
function quotesToast(arrived: PricedQuoteCard[]): string {
  const carriers = [...new Set(arrived.map((quote) => quote.carrierName))]
  if (arrived.length === 1) return `New quote from ${carriers[0]}`
  const from =
    carriers.length <= 2
      ? carriers.join(' and ')
      : `${carriers[0]}, ${carriers[1]}, and ${carriers.length - 2} more`
  return `${arrived.length} new quotes from ${from}`
}

interface QuoteArrivalNotificationsOptions {
  applicationId: string
  enabled: boolean
  /** The customer clicked the notice, which has brought the tab forward. */
  onOpened: () => void
}

/**
 * Tells a customer that carriers' prices have landed on their board: a toast while they are on the
 * tab, a desktop notice while they are away.
 *
 * What the board holds when the page opens is not news. Prices that land while the customer is away
 * gather into one notice, replaced as more come in, until they are back.
 */
export function useQuoteArrivalNotifications(
  quotes: QuoteCardData[],
  { applicationId, enabled, onOpened }: QuoteArrivalNotificationsOptions
) {
  const pricedIds = useRef<Set<string> | null>(null)
  const unseen = useRef<PricedQuoteCard[]>([])

  useEffect(() => {
    if (!enabled) return
    const priced = quotes.filter(isCarrierPrice)
    const alreadyPriced = pricedIds.current
    pricedIds.current = new Set([...(alreadyPriced ?? []), ...priced.flatMap(pricedIndicationIds)])
    if (!alreadyPriced) return

    const arrived = priced.filter((quote) => pricedIndicationIds(quote).some((id) => !alreadyPriced.has(id)))
    if (arrived.length === 0) return
    if (!isReaderAway()) {
      toast.success(quotesToast(arrived))
      return
    }
    if (unseen.current.length === 0) {
      onceReaderReturns(() => {
        unseen.current = []
      })
    }
    unseen.current = [...unseen.current, ...arrived]
    notifyWhileAway({
      ...pricesNotice(unseen.current),
      tag: `cai-quotes-${applicationId}`,
      onClick: onOpened,
    })
  }, [quotes, enabled, applicationId, onOpened])
}
